import { CloseOutlined, MailOutlined, RightOutlined } from "@ant-design/icons";
import { useState } from "react";

import { useLoc } from "@app/shared/lib/hooks";

import { useUIAppState } from "../../UIAppStateProvider";

import styles from "./EmailConfirmationMessage.module.scss";

// dismissed for the browser tab only: the next visit nudges again
const DISMISS_KEY = "school.email-confirmation-dismissed";

const readDismissed = () => {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
};

/**
 * Soft «confirm your email» nudge (the school does not block access): one compact bar above the tab bar on phones,
 * a small card at the bottom right on desktop. The whole bar opens the confirmation form; × hides it for the session.
 */
export const EmailConfirmationMessage: React.FC = () => {
  const UIAppState = useUIAppState();
  const loc = useLoc();
  const [dismissed, setDismissed] = useState(readDismissed);

  if (!UIAppState.isEmailConfirmationMessageVisible || dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // storage blocked: hidden until reload
    }
  };

  return (
    <div className={styles.bar} role="status">
      <button
        type="button"
        className={styles.main}
        onClick={() => UIAppState.setIsEmailConfirmationModalVisible(true)}
      >
        <span className={styles.icon} aria-hidden>
          <MailOutlined />
        </span>
        <span className={styles.text}>{loc.word("You should confirm your email")}</span>
        <span className={styles.action}>
          <span className={styles.actionLabel}>{loc.word("Confirm")}</span>
          <RightOutlined />
        </span>
      </button>
      <button
        type="button"
        className={styles.close}
        onClick={dismiss}
        aria-label={loc.word("Close")}
      >
        <CloseOutlined />
      </button>
    </div>
  );
};
